import { useContext, useLayoutEffect, useRef, useState } from 'react';
import { PlusIcon } from 'assets/icons';
import { AppContext } from 'context';
import { useClickOutside } from 'hooks/click-outside';
import { ButtonInvitationGlow } from 'common/returning-onboarding/button-invitation-glow';
import {
  useAdvanceOnboarding,
  useCompleteOnboardingOnMemoryAdded,
} from 'common/returning-onboarding/onboarding-status';
import { classnames } from 'helpers/utils';
import { NewMenuList, NewMenuListProps } from './new-menu-list';
import { OnboardingTooltip } from './onboarding-tooltip';
import styles from './new-menu.module.scss';

type NewMenuProps = Omit<NewMenuListProps, 'onClose' | 'className'> & {
  onCreateAlbum: () => void;
  // 'fab' is the phone bottom bar's round "+" (as in the mobile app); its
  // menu and tooltip open upwards.
  variant?: 'button' | 'fab';
};

export const NewMenu = ({ variant = 'button', ...props }: NewMenuProps) => {
  const fab = variant === 'fab';
  const [isOpen, setIsOpen] = useState(false);
  const [buttonWidth, setButtonWidth] = useState(0);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { state } = useContext(AppContext);
  const advanceOnboarding = useAdvanceOnboarding();
  // After the returning-customer introduction, New glows and carries a
  // tooltip until the first memory is added (or the tooltip is dismissed).
  const inviting = state.data.user.onboardingStatus === 'intro_seen';
  useCompleteOnboardingOnMemoryAdded(inviting);

  useClickOutside(menuRef, () => setIsOpen(false));

  // Measured before the tooltip shows, so its first frame (the one the
  // introduction's handoff copies) is already centered under the button.
  useLayoutEffect(() => {
    setButtonWidth(buttonRef.current?.offsetWidth ?? 0);
  }, [inviting]);

  return (
    <div className={styles.container} ref={menuRef}>
      <button
        ref={buttonRef}
        type="button"
        className={classnames(fab ? styles.fabButton : styles.newButton, {
          [styles.open]: isOpen && !fab,
        })}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label={fab ? 'New' : undefined}
        aria-expanded={isOpen}
        data-onboarding-new
      >
        {!fab && 'New'}
        <PlusIcon />
      </button>
      <ButtonInvitationGlow active={inviting && !isOpen} />
      {inviting && !isOpen && (
        <OnboardingTooltip
          anchorWidth={buttonWidth}
          above={fab}
          onDismiss={() => advanceOnboarding('completed')}
        />
      )}
      {isOpen && (
        <NewMenuList
          {...props}
          className={classnames({ [styles.dropdownAbove]: fab })}
          onClose={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};
